import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Fade, Slide, Zoom } from "react-awesome-reveal";
import {
  FaBookOpen,
  FaChevronRight,
  FaImage,
  FaLayerGroup,
  FaPlay,
  FaRegStar,
  FaTimes,
} from "react-icons/fa";

import { getFavoriteTechniques } from "../../services/techniquesServices";
import { useUser } from "../Home/UserContext";

const MAX_TECHNIQUES_HOME = 5;
const FALLBACK_TECHNIQUE_COVER = "/img/cover/cover_techniques.jpg";

const TECHNIQUE_STYLES = [
  {
    gradient: "from-sky-600 via-blue-700 to-slate-950",
    soft: "from-sky-500/15 via-blue-500/10 to-transparent",
    chip: "bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200",
  },
  {
    gradient: "from-amber-500 via-orange-600 to-slate-950",
    soft: "from-amber-500/15 via-orange-500/10 to-transparent",
    chip: "bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-200",
  },
  {
    gradient: "from-emerald-600 via-teal-700 to-slate-950",
    soft: "from-emerald-500/15 via-teal-500/10 to-transparent",
    chip:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200",
  },
  {
    gradient: "from-rose-600 via-red-700 to-slate-950",
    soft: "from-rose-500/15 via-red-500/10 to-transparent",
    chip: "bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-200",
  },
  {
    gradient: "from-violet-600 via-indigo-700 to-slate-950",
    soft: "from-violet-500/15 via-indigo-500/10 to-transparent",
    chip:
      "bg-violet-100 text-violet-700 dark:bg-violet-500/10 dark:text-violet-200",
  },
];

const tr = (t, key, fallback) => t(key, { defaultValue: fallback }) || fallback;

const safeParseJson = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch (error) {
    console.warn("Impossible de lire userInfo depuis localStorage", error);
    return null;
  }
};

const getStoredUserId = () => {
  const userInfo = safeParseJson(localStorage.getItem("userInfo"));
  return userInfo?.userId || userInfo?.id || null;
};

const normalizeTechniquesResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.techniques)) return response.techniques;
  if (Array.isArray(response?.favoriteTechniques)) return response.favoriteTechniques;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.techniques)) return response.data.techniques;
  if (Array.isArray(response?.data?.favoriteTechniques)) {
    return response.data.favoriteTechniques;
  }

  return [];
};

const cleanText = (value) => {
  if (!value) return "";

  return String(value)
    .replace(/<[^>]*>/g, "")
    .replaceAll("&nbsp;", " ")
    .trim();
};

const hasValue = (value) => cleanText(value).length > 0;

const getTechniqueTitle = (technique) =>
  cleanText(technique?.nom || technique?.name || technique?.title) ||
  "Technique de judo";

const getTechniqueFamily = (technique) =>
  cleanText(
    technique?.famille ||
      technique?.family ||
      technique?.categorie ||
      technique?.category,
  );

const getTechniqueTranslation = (technique) =>
  cleanText(technique?.traduction || technique?.translation);

const getTechniqueBelt = (technique) =>
  cleanText(technique?.ceinture || technique?.belt);

const getTechniqueInformation = (technique) => cleanText(technique?.information);

const getTechniqueUv2 = (technique) => cleanText(technique?.uv2);

const getTechniqueImage = (technique) =>
  technique?.image1_url ||
  technique?.image_url ||
  technique?.image ||
  technique?.thumbnail_url ||
  FALLBACK_TECHNIQUE_COVER;

const getTechniqueVideo = (technique) =>
  technique?.video_url || technique?.youtube_url || technique?.video || "";

const getTechniqueKey = (technique, index) =>
  technique?.id ||
  technique?.technique_id ||
  `${getTechniqueTitle(technique)}-${index}`;

const getVisibleTechniques = (techniques) =>
  techniques.filter(Boolean).slice(0, MAX_TECHNIQUES_HOME);

function TechniquesSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
      {[1, 2, 3, 4, 5].map((item) => (
        <div
          key={item}
          className="overflow-hidden rounded-[1.7rem] border border-slate-200 bg-white p-2 shadow-xl shadow-slate-200/50 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20"
        >
          <div className="h-40 animate-pulse rounded-[1.35rem] bg-slate-200 dark:bg-white/10 sm:h-44 lg:h-40" />

          <div className="space-y-2 p-2 pt-3">
            <div className="h-4 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-white/10" />
            <div className="h-3 w-1/2 animate-pulse rounded-full bg-slate-200 dark:bg-white/10" />
          </div>
        </div>
      ))}
    </div>
  );
}

function TechniquesEmptyState({ isConnected, onAction, onExplore, t }) {
  return (
    <Fade direction="up" triggerOnce>
      <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
        <div className="grid gap-4 rounded-[1.6rem] bg-slate-50 p-4 dark:bg-white/[0.04] md:grid-cols-[0.85fr_1.15fr] md:items-center">
          <div className="relative min-h-52 overflow-hidden rounded-[1.4rem] bg-slate-900 md:min-h-60">
            <img
              src={FALLBACK_TECHNIQUE_COVER}
              alt="Bibliothèque de techniques Judo Coach Pro"
              className="absolute inset-0 h-full w-full object-cover opacity-80 transition duration-700 hover:scale-105"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/35 to-transparent" />

            <div className="absolute bottom-4 left-4 right-4 text-white">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] backdrop-blur-xl">
                <FaBookOpen />
                Techniques
              </div>

              <p className="mt-3 font-heading text-xl font-bold leading-tight sm:text-2xl">
                {isConnected
                  ? tr(
                      t,
                      "techniquesFavorites.emptyVisualTitle",
                      "Vos favoris apparaîtront ici",
                    )
                  : tr(
                      t,
                      "techniquesFavorites.visitorVisualTitle",
                      "Créez votre carnet technique",
                    )}
              </p>
            </div>
          </div>

          <div className="px-1 py-2 md:px-3">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700 dark:text-sky-200">
              {isConnected
                ? tr(t, "techniquesFavorites.noTechniquesEyebrow", "Aucun favori")
                : tr(
                    t,
                    "techniquesFavorites.visitorEyebrow",
                    "Progression technique",
                  )}
            </p>

            <h3 className="mt-3 font-heading text-2xl font-bold leading-tight text-slate-950 dark:text-white">
              {isConnected
                ? tr(
                    t,
                    "techniquesFavorites.noTechniquesTitle",
                    "Ajoutez vos techniques préférées pour les retrouver rapidement.",
                  )
                : tr(
                    t,
                    "techniquesFavorites.notConnectedTitle",
                    "Connectez-vous pour construire votre sélection personnelle.",
                  )}
            </h3>

            <p className="mt-3 max-w-xl text-sm font-medium leading-6 text-slate-600 dark:text-white/55">
              {isConnected
                ? tr(
                    t,
                    "techniquesFavorites.noTechniquesDescription",
                    "Marquez en favori les techniques que vous souhaitez travailler : projections, immobilisations, clés, étranglements ou enchaînements.",
                  )
                : tr(
                    t,
                    "techniquesFavorites.notConnectedDescription",
                    "Judo Coach Pro garde vos techniques favorites à portée de main pour réviser avant un cours, un passage de grade ou une compétition.",
                  )}
            </p>

            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={onAction}
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-sky-400 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-sky-500/20 transition hover:-translate-y-0.5 active:translate-y-0 sm:w-auto"
              >
                {isConnected
                  ? tr(
                      t,
                      "techniquesFavorites.exploreTechniques",
                      "Explorer les techniques",
                    )
                  : tr(
                      t,
                      "techniquesFavorites.loginOrRegister",
                      "Créer mon compte",
                    )}
                <FaChevronRight className="text-xs" />
              </button>

              {/* {!isConnected && (
                <button
                  type="button"
                  onClick={onExplore}
                  className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md active:scale-[0.99] dark:border-white/10 dark:bg-white/[0.07] dark:text-white dark:hover:bg-white/10 sm:w-auto"
                >
                  {tr(
                    t,
                    "techniquesFavorites.discoverLibrary",
                    "Voir la bibliothèque",
                  )}
                  <FaBookOpen className="text-xs" />
                </button>
              )} */}
            </div>
          </div>
        </div>
      </div>
    </Fade>
  );
}

function TechniqueCard({ technique, index, onTechniqueClick }) {
  const style = TECHNIQUE_STYLES[index % TECHNIQUE_STYLES.length];
  const title = getTechniqueTitle(technique);
  const family = getTechniqueFamily(technique);
  const translation = getTechniqueTranslation(technique);
  const belt = getTechniqueBelt(technique);
  const information = getTechniqueInformation(technique);
  const imageUrl = getTechniqueImage(technique);
  const videoUrl = getTechniqueVideo(technique);
  const hasVideo = hasValue(videoUrl);

  const openVideo = (event) => {
    event.stopPropagation();

    if (hasVideo) {
      window.open(videoUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Zoom triggerOnce delay={index * 60} className="h-full">
      <article className="group relative h-full overflow-hidden rounded-[1.7rem] border border-slate-200 bg-white p-2 shadow-xl shadow-slate-200/50 transition duration-300 hover:-translate-y-1 hover:shadow-2xl dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
        <div
          className={`pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-br ${style.soft}`}
        />

        <button
          type="button"
          onClick={() => onTechniqueClick(technique)}
          className="relative block w-full overflow-hidden rounded-[1.35rem] bg-slate-100 text-left dark:bg-white/10"
          aria-label={`Voir le détail de ${title}`}
        >
          <img
            src={imageUrl}
            alt={title}
            className="h-60 w-full object-cover transition duration-700 group-hover:scale-105 sm:h-44 lg:h-60"
            loading="lazy"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent opacity-80" />

          {hasVideo && (
            <button
              type="button"
              onClick={openVideo}
              className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-2xl border border-white/20 bg-white/15 text-white backdrop-blur-xl transition hover:bg-white hover:text-slate-950 active:scale-95"
              aria-label={`Voir la vidéo de ${title}`}
            >
              <FaPlay className="text-xs" />
            </button>
          )}

          <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-2">
            {family && (
              <span
                className={`max-w-full truncate rounded-full px-3 py-1 text-xs font-bold ${style.chip}`}
              >
                {family}
              </span>
            )}

            {belt && (
              <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-slate-700 shadow-sm">
                {belt}
              </span>
            )}
          </div>
        </button>

        <div className="relative p-2 pt-3">
          <h3 className="line-clamp-2 font-heading text-lg font-bold leading-tight text-slate-950 dark:text-white">
            {title}
          </h3>

          {translation && (
            <p className="mt-1 line-clamp-1 text-sm font-bold text-sky-700 dark:text-sky-200">
              {translation}
            </p>
          )}

          {information && (
            <p className="mt-2 line-clamp-2 text-xs font-medium leading-5 text-slate-600 dark:text-white/55">
              {information}
            </p>
          )}

          <button
            type="button"
            onClick={() => onTechniqueClick(technique)}
            className="mt-3 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-white/10 dark:bg-white/[0.07] dark:text-white dark:hover:bg-white/10"
          >
            Détails
            <FaImage className="text-xs" />
          </button>
        </div>
      </article>
    </Zoom>
  );
}

function TechniqueDetailModal({ technique, onClose }) {
  if (!technique) return null;

  const title = getTechniqueTitle(technique);
  const family = getTechniqueFamily(technique);
  const translation = getTechniqueTranslation(technique);
  const belt = getTechniqueBelt(technique);
  const information = getTechniqueInformation(technique);
  const uv2 = getTechniqueUv2(technique);
  const imageUrl = getTechniqueImage(technique);
  const videoUrl = getTechniqueVideo(technique);
  const hasVideo = hasValue(videoUrl);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/85 p-3 backdrop-blur-sm sm:p-4"
      onClick={onClose}
    >
      <Fade direction="up" duration={250} triggerOnce>
        <div
          className="relative max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-[1.8rem] bg-white p-3 shadow-2xl dark:bg-slate-950 sm:p-4"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-slate-950 shadow-xl transition hover:scale-105 active:scale-95 dark:bg-slate-900 dark:text-white"
            aria-label="Fermer la fiche technique"
          >
            <FaTimes />
          </button>

          <div className="grid gap-4 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
            <div className="overflow-hidden rounded-[1.4rem] bg-slate-100 dark:bg-white/10">
              <img
                src={imageUrl}
                alt={title}
                className="max-h-[68vh] w-full object-contain"
              />
            </div>

            <div className="p-1 pr-0 sm:p-3 sm:pr-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-sky-700 dark:border-sky-400/20 dark:bg-sky-500/10 dark:text-sky-200">
                <FaLayerGroup />
                Technique favorite
              </div>

              <h3 className="mt-4 font-heading text-2xl font-bold leading-tight text-slate-950 dark:text-white sm:text-3xl">
                {title}
              </h3>

              {translation && (
                <p className="mt-2 text-base font-bold leading-7 text-sky-700 dark:text-sky-200">
                  {translation}
                </p>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {family && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700 dark:bg-white/10 dark:text-white/70">
                    Famille : {family}
                  </span>
                )}

                {belt && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700 dark:bg-white/10 dark:text-white/70">
                    Ceinture : {belt}
                  </span>
                )}
              </div>

              {information && (
                <div className="mt-5 rounded-[1.4rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                    Informations
                  </p>

                  <p className="mt-2 whitespace-pre-line text-sm font-medium leading-6 text-slate-600 dark:text-white/60">
                    {information}
                  </p>
                </div>
              )}

              {uv2 && (
                <div className="mt-3 rounded-[1.4rem] border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                    UV2 / programme technique
                  </p>

                  <p className="mt-2 whitespace-pre-line text-sm font-medium leading-6 text-slate-600 dark:text-white/60">
                    {uv2}
                  </p>
                </div>
              )}

              {hasVideo && (
                <a
                  href={videoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 active:translate-y-0 dark:bg-white dark:text-slate-950 sm:w-auto"
                >
                  Voir la vidéo
                  <FaPlay className="text-xs" />
                </a>
              )}
            </div>
          </div>
        </div>
      </Fade>
    </div>
  );
}

function TechniquesFavoritesHome({ isConnected }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();

  const [techniques, setTechniques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTechnique, setSelectedTechnique] = useState(null);

  const userId = useMemo(
    () => user?.userId || user?.id || getStoredUserId(),
    [user?.id, user?.userId],
  );

  const sectionContent = useMemo(() => {
    if (isConnected) {
      return {
        eyebrow: tr(t, "techniquesFavorites.connectedEyebrow", "Mes favoris"),
        title: tr(
          t,
          "techniquesFavorites.connectedTitle",
          "Mes techniques favorites.",
        ),
        subtitle: tr(
          t,
          "techniquesFavorites.connectedSubtitle",
          "Accédez rapidement aux techniques enregistrées dans votre carnet personnel.",
        ),
        note: null,
        primaryLabel: tr(
          t,
          "techniquesFavorites.seeAllTechniques",
          "Voir toutes les techniques",
        ),
        primaryPath: "/techniques-judo",
      };
    }

    return {
      eyebrow: tr(
        t,
        "techniquesFavorites.visitorSectionEyebrow",
        "Carnet technique",
      ),
      title: tr(
        t,
        "techniquesFavorites.visitorSectionTitle",
        "Vos techniques à portée de main.",
      ),
      subtitle: tr(
        t,
        "techniquesFavorites.visitorSectionSubtitle",
        "Favoris, supports visuels, vidéos et révisions ciblées pour progresser plus facilement.",
      ),
      note: tr(
        t,
        "techniquesFavorites.visitorNote",
        "Connectez-vous pour afficher ici vos techniques favorites personnelles.",
      ),
      primaryLabel: tr(
        t,
        "techniquesFavorites.loginOrRegister",
        "Créer mon compte gratuit",
      ),
      primaryPath: "/register",
    };
  }, [isConnected, t]);

  const handlePrimaryNavigation = () => {
    navigate(sectionContent.primaryPath);
  };

  const handleExploreNavigation = () => {
    navigate("/techniques-judo");
  };

  useEffect(() => {
    let isMounted = true;

    const fetchFavoriteTechniques = async () => {
      if (!isConnected || !userId) {
        if (isMounted) {
          setTechniques([]);
          setLoading(false);
        }
        return;
      }

      setLoading(true);

      try {
        const response = await getFavoriteTechniques(userId);
        const normalizedTechniques = normalizeTechniquesResponse(response);

        if (isMounted) {
          setTechniques(getVisibleTechniques(normalizedTechniques));
        }
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des techniques favorites :",
          error,
        );

        if (isMounted) {
          setTechniques([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchFavoriteTechniques();

    return () => {
      isMounted = false;
    };
  }, [isConnected, userId]);

  const hasTechniques = techniques.length > 0;

  return (
    <section className="relative overflow-hidden bg-slate-50 px-4 py-12 text-slate-950 dark:bg-[#07111f] dark:text-white sm:px-6 sm:py-14 lg:px-8">
      <div className="absolute -left-24 top-12 h-64 w-64 rounded-full bg-sky-500/10 blur-3xl" />
      <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-amber-500/10 blur-3xl dark:bg-amber-400/10" />

      <div className="relative mx-auto max-w-7xl">
        <div className="mb-6 grid gap-5 lg:grid-cols-[0.72fr_0.28fr] lg:items-end">
          <Slide direction="left" triggerOnce>
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-sky-700 shadow-sm dark:border-sky-400/20 dark:bg-sky-500/10 dark:text-sky-200">
                <FaRegStar />
                {sectionContent.eyebrow}
              </div>

              <h2 className="mt-4 font-heading text-3xl font-bold leading-tight text-slate-950 dark:text-white sm:text-4xl lg:text-5xl">
                {sectionContent.title}
              </h2>

              <p className="mt-3 max-w-2xl text-base font-medium leading-7 text-slate-600 dark:text-white/55">
                {sectionContent.subtitle}
              </p>
            </div>
          </Slide>

          <Fade direction="up" triggerOnce delay={120}>
            <div className="flex lg:justify-end">
              <button
                type="button"
                onClick={handlePrimaryNavigation}
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-sky-400 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-sky-500/20 transition hover:-translate-y-0.5 active:translate-y-0 sm:w-auto"
              >
                {sectionContent.primaryLabel}
                <FaChevronRight className="text-xs" />
              </button>
            </div>
          </Fade>
        </div>

        {sectionContent.note && (
          <Fade direction="up" triggerOnce>
            <div className="mb-5 rounded-[1.4rem] border border-sky-200 bg-sky-50 p-3 text-sm font-semibold leading-6 text-sky-800 dark:border-sky-400/20 dark:bg-sky-500/10 dark:text-sky-100">
              {sectionContent.note}
            </div>
          </Fade>
        )}

        {loading && <TechniquesSkeleton />}

        {!loading && !hasTechniques && (
          <TechniquesEmptyState
            isConnected={isConnected}
            onAction={handlePrimaryNavigation}
            onExplore={handleExploreNavigation}
            t={t}
          />
        )}

        {!loading && hasTechniques && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {techniques.map((technique, index) => (
              <TechniqueCard
                key={getTechniqueKey(technique, index)}
                technique={technique}
                index={index}
                onTechniqueClick={setSelectedTechnique}
              />
            ))}
          </div>
        )}
      </div>

      <TechniqueDetailModal
        technique={selectedTechnique}
        onClose={() => setSelectedTechnique(null)}
      />
    </section>
  );
}

export default TechniquesFavoritesHome;